import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine } from "recharts";

const COLORS = { TKA: "#E25E3E", UTBK: "#4A6B53" };

const shortDate = (s) => {
  try { return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }); }
  catch { return s; }
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload.find((x) => x.value != null);
  if (!p) return null;
  const d = p.payload;
  return (
    <div className="rounded-xl bg-[#1A1C1A] text-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold">{d.nama}</p>
      <p className="text-white/70">{d.kategori} · {shortDate(d.created_at)}</p>
      <p className="mt-0.5">Skor: <b className="text-[#F4B942]">{p.value}</b></p>
    </div>
  );
};

export const ScoreTrendChart = ({ data = [], height = 220, showBands = true, targetLine = null, targetLabel = null }) => {
  const [filter, setFilter] = useState("all");

  if (data.length === 0)
    return <p className="text-sm text-[#5C5F5C]">Belum ada data TryOut. Kerjakan TryOut untuk melihat tren skormu.</p>;
  if (data.length === 1)
    return <p className="text-sm text-[#5C5F5C]">Skor TryOut pertama: <b className="text-[#4A6B53]">{data[0].skor}</b>. Kerjakan minimal satu TryOut lagi untuk melihat grafik trennya.</p>;

  const cats = Array.from(new Set(data.map((d) => d.kategori))).filter(Boolean);
  const hasBoth = cats.includes("TKA") && cats.includes("UTBK");
  const rows = data.map((d, i) => ({
    label: `${i + 1}. ${shortDate(d.created_at)}`,
    created_at: d.created_at, nama: d.nama, kategori: d.kategori,
    TKA: d.kategori === "TKA" ? d.skor : null,
    UTBK: d.kategori === "UTBK" ? d.skor : null,
  }));
  const showTKA = (filter === "all" || filter === "TKA") && cats.includes("TKA");
  const showUTBK = (filter === "all" || filter === "UTBK") && cats.includes("UTBK");

  return (
    <div data-testid="score-trend-chart">
      {hasBoth && (
        <div className="flex items-center gap-2 mb-3" data-testid="trend-filter">
          {[{ k: "all", l: "Semua" }, { k: "TKA", l: "TKA" }, { k: "UTBK", l: "UTBK" }].map((o) => (
            <button key={o.k} onClick={() => setFilter(o.k)} data-testid={`trend-filter-${o.k}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === o.k ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "border-[#E5E0D8] text-[#5C5F5C] hover:border-[#1A1C1A]/40"}`}>
              {o.k !== "all" && <span className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle" style={{ background: COLORS[o.k] }} />}{o.l}
            </button>
          ))}
        </div>
      )}
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 12, left: -4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
            {showBands && (<>
              <ReferenceArea y1={0} y2={60} fill="#D9381E" fillOpacity={0.05} ifOverflow="hidden" />
              <ReferenceArea y1={60} y2={80} fill="#F4B942" fillOpacity={0.08} ifOverflow="hidden" />
              <ReferenceArea y1={80} y2={100} fill="#4A6B53" fillOpacity={0.08} ifOverflow="hidden" />
            </>)}
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#5C5F5C" }} tickLine={false} axisLine={{ stroke: "#E5E0D8" }} interval="preserveStartEnd" />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11, fill: "#5C5F5C" }} tickLine={false} axisLine={false} width={46} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E25E3E", strokeDasharray: "4 4" }} />
            {targetLine != null && (
              <ReferenceLine y={targetLine} stroke="#1A1C1A" strokeDasharray="6 4" strokeWidth={2}
                label={{ value: targetLabel || `Target ${targetLine}`, position: "insideTopRight", fontSize: 10, fill: "#1A1C1A", fontWeight: 600 }} />
            )}
            {showTKA && <Line type="monotone" dataKey="TKA" name="TKA" stroke={COLORS.TKA} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.TKA, strokeWidth: 0 }} activeDot={{ r: 6, fill: COLORS.TKA }} connectNulls />}
            {showUTBK && <Line type="monotone" dataKey="UTBK" name="UTBK" stroke={COLORS.UTBK} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.UTBK, strokeWidth: 0 }} activeDot={{ r: 6, fill: COLORS.UTBK }} connectNulls />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {showBands && <p className="text-[11px] text-[#5C5F5C] mt-2">Zona pencapaian TKA: <span className="text-[#D9381E]">Kurang &lt;60</span> · <span className="text-[#B8860B]">Cukup 60–79</span> · <span className="text-[#4A6B53]">Baik ≥80</span></p>}
    </div>
  );
};

export default ScoreTrendChart;
